/**
 * Заказы: доска аптеки (`GET /admin/fulfillment/board`), таблица
 * (`GET /admin/orders`), карточка и переходы по статусам.
 *
 * Колонки доски задаёт сервер — и их состав, и порядок, и заголовки: так
 * экран не разойдётся с потоком статусов, когда тот поменяется (R-45).
 * Перетаскивание карточки идёт одной ручкой `POST /admin/orders/{id}/status`,
 * а не набором отдельных действий (R-40).
 */
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { ApiClient, components } from '@dorixona/api'
import { parseMs as toMs } from '@dorixona/utils'
import { useApi } from '../../api/context'
import { keys } from '../../api/keys'
import { toLedger, type LedgerEntry } from '../customers/api'

type OrderDto = components['schemas']['Order']
type BoardCardDto = components['schemas']['BoardCard']
type LineDto = components['schemas']['OrderLine']
type EventDto = components['schemas']['OrderEvent']

export type OrderStatus = NonNullable<OrderDto['status']>
export type ShipMethod = 'pickup' | 'delivery'

export interface OrderLine {
  id: number
  productId: number
  name: string
  price: number
  qty: number
  total: number
  cashback: number
  /** Позиция собрана: по ним считается прогресс сборки (R-42). */
  picked: boolean
}

export interface OrderEvent {
  id: number
  message: string
  /** Кто сделал: клиент, сотрудник, система или провайдер. */
  actor: NonNullable<EventDto['actor_type']>
  at: number
}

export interface Courier {
  name: string
  phone: string
  /** Цвет, марка и модель одной строкой: «белый Chevrolet Cobalt». */
  car: string
  carNumber: string
}

/** Заявка курьеру у службы доставки. Есть только у доставки и только после вызова курьера. */
export interface Delivery {
  /** Служба: `millenium`, `stub`… */
  provider: string
  tracking: string
  /** Что служба сообщила о курьере; пусто — машину ещё ищут (R-44). */
  status: string
  courier: Courier | null
}

export interface Order {
  id: number
  /** Номер для людей, с 1001. */
  number: number
  customerId: number
  customerName: string
  customerPhone: string
  status: OrderStatus
  ship: ShipMethod
  storeId: number
  storeName: string
  /** Адрес доставки одной строкой; пустая строка — самовывоз. */
  address: string
  payMethod: string
  payStatus: string
  goods: number
  deliveryFee: number
  /** Оплачено баллами, сум. */
  spend: number
  /** Начисленный кэшбэк, сум. */
  cashback: number
  total: number
  hasRx: boolean
  assigneeId: number
  createdAt: number
  completedAt: number
  /** Когда пришла оплата, мс; 0 — ещё не платили. */
  paidAt: number
  /** До какого времени бронь ждёт клиента, мс; 0 — брони нет (R-39). */
  reservationExpires: number
  lines: OrderLine[]
  events: OrderEvent[]
  delivery: Delivery | null
}

export interface Sla {
  stage: string
  minutes: number
  /** Отрицательное — срок уже вышел. */
  minutesLeft: number
  breached: boolean
}

export interface BoardCard extends Order {
  /** Таймер этапа; null — у этапа нет лимита в настройках (BR-20). */
  sla: Sla | null
}

export interface BoardColumn {
  key: string
  title: string
  /** Сколько всего в колонке: карточек сервер отдаёт не больше страницы. */
  total: number
  orders: BoardCard[]
}

/** Адрес доставки приходит объектом произвольной формы — нас интересует подпись. */
export function addressText(address: OrderDto['address']): string {
  if (!address || typeof address !== 'object') return ''
  const label = (address as { label?: unknown }).label
  return typeof label === 'string' ? label : ''
}

function toLine(dto: LineDto): OrderLine {
  return {
    id: dto.id ?? 0,
    productId: dto.product_id ?? 0,
    name: dto.name ?? '',
    price: dto.price ?? 0,
    qty: dto.qty ?? 0,
    total: dto.line_total ?? 0,
    cashback: dto.line_cashback ?? 0,
    picked: Boolean(dto.picked_at),
  }
}

function toEvent(dto: EventDto): OrderEvent {
  return {
    id: dto.id ?? 0,
    message: dto.message ?? '',
    actor: dto.actor_type ?? 'system',
    at: toMs(dto.created_at),
  }
}

export function toDelivery(dto: OrderDto['delivery']): Delivery | null {
  if (!dto) return null
  const c = dto.courier
  return {
    provider: dto.provider ?? '',
    tracking: dto.tracking_number ?? '',
    status: dto.status ?? '',
    courier: c
      ? {
          name: c.name ?? '',
          phone: c.phone ?? '',
          car: [c.car_color, c.car_mark, c.car_model].filter(Boolean).join(' '),
          carNumber: c.car_number ?? '',
        }
      : null,
  }
}

export function toOrder(dto: OrderDto): Order {
  return {
    id: dto.id ?? 0,
    number: dto.number ?? 0,
    customerId: dto.customer_id ?? 0,
    customerName: dto.customer ?? '',
    customerPhone: dto.customer_phone ?? '',
    status: dto.status ?? 'new',
    ship: dto.ship_method ?? 'pickup',
    storeId: dto.store_id ?? 0,
    storeName: dto.store ?? '',
    address: addressText(dto.address),
    payMethod: dto.pay_method ?? '',
    payStatus: dto.pay_status ?? '',
    goods: dto.goods_total ?? 0,
    deliveryFee: dto.delivery_fee ?? 0,
    spend: dto.spend_amount ?? 0,
    cashback: dto.cashback_amount ?? 0,
    total: dto.total_to_pay ?? 0,
    hasRx: dto.has_rx ?? false,
    assigneeId: dto.assignee_staff_id ?? 0,
    createdAt: toMs(dto.created_at),
    completedAt: toMs(dto.completed_at),
    paidAt: toMs(dto.paid_at),
    reservationExpires: toMs(dto.reservation_expires_at),
    lines: (dto.lines ?? []).map(toLine),
    events: (dto.events ?? []).map(toEvent),
    delivery: toDelivery(dto.delivery),
  }
}

export function toBoardCard(dto: BoardCardDto): BoardCard {
  return {
    ...toOrder(dto),
    sla: dto.sla
      ? {
          stage: dto.sla.stage ?? '',
          minutes: dto.sla.minutes ?? 0,
          minutesLeft: dto.sla.minutes_left ?? 0,
          breached: dto.sla.breached ?? false,
        }
      : null,
  }
}

export async function fetchBoard(
  api: ApiClient,
  storeId: number,
  ship: ShipMethod | '',
): Promise<BoardColumn[]> {
  const board = await api.get<{
    columns?: { key?: string; title?: string; total?: number; orders?: BoardCardDto[] }[]
  }>('/admin/fulfillment/board', {
    query: { store_id: storeId || undefined, ship: ship || undefined },
  })
  return (board.columns ?? []).map((c) => ({
    key: c.key ?? '',
    title: c.title ?? '',
    total: c.total ?? 0,
    orders: (c.orders ?? []).map(toBoardCard),
  }))
}

export interface OrderQuery {
  q?: string
  status?: string
  ship?: ShipMethod | ''
  payMethod?: string
  storeId?: number
  customerId?: number
  productId?: number
  page?: number
  /** Строк на странице; по умолчанию 10 — списки листаются страницами. */
  pageSize?: number
}

export interface OrderPage {
  items: Order[]
  total: number
}

/** Отбор без страниц: им же фильтруется и выгрузка в CSV. */
function toFilterQuery(query: OrderQuery) {
  return {
    q: query.q || undefined,
    status: query.status || undefined,
    ship: query.ship || undefined,
    pay_method: query.payMethod || undefined,
    store_id: query.storeId || undefined,
    customer_id: query.customerId || undefined,
    product_id: query.productId || undefined,
  }
}

function toQuery(query: OrderQuery) {
  return { ...toFilterQuery(query), page: query.page ?? 1, page_size: query.pageSize ?? 10 }
}

export async function fetchOrders(api: ApiClient, query: OrderQuery): Promise<OrderPage> {
  const page = await api.get<{ items?: OrderDto[]; total?: number }>('/admin/orders', {
    query: toQuery(query),
  })
  return { items: (page.items ?? []).map(toOrder), total: page.total ?? 0 }
}

/**
 * Выгрузка в Excel (`GET /admin/orders/export?format=xlsx`): тот же отбор, что
 * у таблицы, но без страниц — в файл идёт вся выборка, а не видимые десять строк.
 */
export function exportOrders(api: ApiClient, query: OrderQuery): Promise<Blob> {
  return api.download('/admin/orders/export', {
    query: { ...toFilterQuery(query), format: 'xlsx' },
  })
}

/** Файл нужен по кнопке, а не при открытии экрана, — поэтому функция, а не запрос. */
export function useExportOrders() {
  const api = useApi()
  return (query: OrderQuery) => exportOrders(api, query)
}

export function useBoard(storeId: number, ship: ShipMethod | '', enabled = true) {
  const api = useApi()
  return useQuery({
    queryKey: keys.board(storeId, ship),
    queryFn: () => fetchBoard(api, storeId, ship),
    enabled,
  })
}

/**
 * `keepPrevious` — для списка с поиском и страницами: пока идёт новая
 * выборка, видна прежняя, а не скелет на каждую букву. Карточкам клиента,
 * аптеки и товара это не нужно: там чужой список хуже пустого.
 */
export function useOrders(query: OrderQuery, enabled = true, keepPrevious = false) {
  const api = useApi()
  return useQuery({
    queryKey: keys.orders(toQuery(query)),
    queryFn: () => fetchOrders(api, query),
    enabled,
    placeholderData: keepPrevious ? keepPreviousData : undefined,
  })
}

/** Как часто перечитывать карточку, пока заказ у курьера. */
export const TRACK_MS = 30_000

/** Заказ ждёт курьера или уже едет: его стоит перечитывать. */
export function isTracked(order: Order): boolean {
  return order.status === 'awaiting_courier' || order.status === 'in_delivery'
}

export function useOrder(id: number) {
  const api = useApi()
  return useQuery({
    queryKey: keys.order(id),
    queryFn: async () => toOrder(await api.get<OrderDto>(`/admin/orders/${id}`)),
    enabled: id > 0,
    // Пока заказ у курьера, статус и водителя присылает служба — перечитываем карточку.
    refetchInterval: (query) =>
      query.state.data && isTracked(query.state.data) ? TRACK_MS : false,
  })
}

export function useOrderTransactions(id: number, enabled: boolean) {
  const api = useApi()
  return useQuery({
    queryKey: [...keys.order(id), 'transactions'],
    queryFn: async () => {
      const page = await api.get<{ items?: components['schemas']['LedgerEntry'][] }>(
        `/admin/orders/${id}/transactions`,
      )
      return (page.items ?? []).map(toLedger) satisfies LedgerEntry[]
    },
    enabled,
  })
}

/**
 * Любое действие над заказом возвращает заказ целиком: кладём его в кэш
 * карточки и обновляем доску и таблицу — статус мог поменять и соседние числа.
 */
function useOrderAction<T>(run: (api: ApiClient, input: T) => Promise<OrderDto>) {
  const api = useApi()
  const client = useQueryClient()
  return useMutation({
    mutationFn: async (input: T) => toOrder(await run(api, input)),
    onSuccess: async (order) => {
      client.setQueryData(keys.order(order.id), order)
      await client.invalidateQueries({ queryKey: ['board'] })
      await client.invalidateQueries({ queryKey: ['orders'] })
    },
  })
}

export interface StatusChange {
  id: number
  to: OrderStatus
  /** Оплата в аптеке: деньги записываются на фармацевта (R-34). */
  cashCollected?: boolean
}

export function useChangeStatus() {
  return useOrderAction<StatusChange>((api, { id, to, cashCollected }) =>
    api.post<OrderDto>(`/admin/orders/${id}/status`, {
      to,
      cash_collected: cashCollected ?? undefined,
    }),
  )
}

export function useTakeOrder() {
  return useOrderAction<number>((api, id) => api.post<OrderDto>(`/admin/orders/${id}/take`))
}

export function usePickLine() {
  return useOrderAction<{ id: number; lineId: number }>((api, { id, lineId }) =>
    api.post<OrderDto>(`/admin/orders/${id}/pick`, { line_id: lineId }),
  )
}

export function useAssemble() {
  return useOrderAction<number>((api, id) => api.post<OrderDto>(`/admin/orders/${id}/assemble`))
}

export function useIssueOrder() {
  return useOrderAction<{ id: number; cashCollected: boolean }>((api, { id, cashCollected }) =>
    api.post<OrderDto>(`/admin/orders/${id}/issue`, { cash_collected: cashCollected }),
  )
}

export type DeclineReason = 'out_of_stock' | 'damaged' | 'other'

export function useDeclineOrder() {
  return useOrderAction<{ id: number; reason: DeclineReason; comment: string }>(
    (api, { id, reason, comment }) =>
      api.post<OrderDto>(`/admin/orders/${id}/decline`, { reason, comment }),
  )
}

/** Ручная маршрутизация: доступна только сотруднику всей сети (R-38). */
export function useRouteOrder() {
  return useOrderAction<{ id: number; storeId: number }>((api, { id, storeId }) =>
    api.post<OrderDto>(`/admin/orders/${id}/route`, { store_id: storeId }),
  )
}

/**
 * Отмена заказа, оплаченного сохранённой картой: заблокированные деньги
 * возвращаются на карту, баллы и товар — назад (R-105). Только сотрудник всей
 * сети и только пока деньги заблокированы, а товар не ушёл.
 */
export function useReleaseHold() {
  return useOrderAction<number>((api, id) => api.post<OrderDto>(`/admin/orders/${id}/release`))
}

/** Собранную доставку отдают курьеру: заявка уходит провайдеру заново (R-43). */
export function useCallCourier() {
  return useOrderAction<number>((api, id) => api.post<OrderDto>(`/admin/orders/${id}/call-courier`))
}

/** Бумажный лист заказа (F-35): сам заказ, реквизиты сети и время печати. */
export interface PrintSheet {
  order: Order
  /** Реквизиты из настроек: название, телефон и почта поддержки. */
  organization: Record<string, string>
  printedAt: number
}

/**
 * Лист печатается по кнопке, поэтому это действие, а не запрос карточки:
 * данные нужны ровно в момент печати, а `printed_at` ставит сервер.
 */
export function usePrintSheet() {
  const api = useApi()
  return useMutation({
    mutationFn: async (id: number): Promise<PrintSheet> => {
      const sheet = await api.get<{
        order?: OrderDto
        organization?: Record<string, string>
        printed_at?: string
      }>(`/admin/orders/${id}/print`)
      return {
        order: toOrder(sheet.order ?? {}),
        organization: sheet.organization ?? {},
        printedAt: toMs(sheet.printed_at),
      }
    },
  })
}
